// " So: the TOKEN is what crosses the wire and is what we store/send; the LABEL is display only and
// never travels. Do not add a translation layer, and do not send a label.

// Added to the ONE shared token set rather than a parallel one — same rule as every other class
// here.
export const BULK_CLASSES = [
  "Boundary",
  "Zone",
  "SamplePoint",
  "ECScanReading",
  "YieldData",
  "Other",
] as const;
export type BulkClass = (typeof BULK_CLASSES)[number];

/* * The ones the user is told we detect; `Other` is the "you tell us" bucket, so it isn't offered as a detection. */
export const DETECTED_CLASSES = BULK_CLASSES.filter(
  (c) => c !== "Other",
) as readonly BulkClass[];

const CLASS_LABELS: Record<BulkClass, string> = {
  Boundary: "Boundary",
  Zone: "Zone",
  SamplePoint: "Sample Point",
  ECScanReading: "EC Scan Reading",
  YieldData: "Yield / Harvest",
  Other: "Other",
};
export function classLabel(c: string): string {
  return CLASS_LABELS[c as BulkClass] ?? c;
}
export function isBulkClass(c: unknown): c is BulkClass {
  return (
    typeof c === "string" && (BULK_CLASSES as readonly string[]).includes(c)
  );
}

// Do not re-add verdict UI; /conflicts survives server-side as a read-only diagnostic only.

// `class_confidence` is a REAL 0..1 column, but compute writes an ORDINAL encoding of a two-level classifier
// (high -> 0.9, low -> 0.4): "Nothing should read it as a calibrated probability; the FE gates on the class and
// shows `class_signals`." Hence: no percentage anywhere in the UI — a coarse flag, and the reasons.
export function confidenceIsLow(conf: number | null | undefined): boolean {
  return typeof conf === "number" && Number.isFinite(conf) && conf < 0.75;
}

/*
 *
 * An object needs the user when we didn't classify it, we classified it weakly, or the server explicitly asked.
 * `needs_user` from the server always wins — it covers cases we can't see from the class alone (e.g. an
 * uncertifiable candidate that must never read as "no collision").
 */
export function needsUserClass(o: {
  proposed_class?: string | null;
  user_class?: string | null;
  class_confidence?: number | null;
  needs_user?: boolean | null;
}): boolean {
  if (o.user_class) return false; // already answered
  if (o.needs_user) return true;
  if (!o.proposed_class || o.proposed_class === "Other") return true;
  return confidenceIsLow(o.class_confidence);
}

export const ORPHAN_REASONS = ["no_container", "discarded_boundary"] as const;
export type OrphanReason = (typeof ORPHAN_REASONS)[number];

export function orphanReasonText(
  reason: string | null | undefined,
  boundaryName?: string | null,
): string {
  if (reason === "discarded_boundary") {
    const which = boundaryName ? `“${boundaryName}”` : "a boundary";
    return `Its only container was ${which}, which you chose not to bring in.`;
  }
  if (reason === "no_container")
    return "It didn’t fall inside any field or any boundary being brought in.";
  return "It has nowhere to go."; // unknown/absent reason still renders a sentence, never a blank
}
/* * Only `discarded_boundary` can be undone by revisiting the overlap step — `no_container` has no decision behind it. */
export function orphanIsRecoverable(
  reason: string | null | undefined,
): boolean {
  return reason === "discarded_boundary";
}

// ---- Session state machine (DB migration 050, LIVE) ------------------------
// The session IS the wizard's state: compute runs a short job per stage and parks in an `awaiting_*` status when
// it needs the user. So the FE never invents a step — it renders whatever `session.status` says, which is also
// what makes a page reload a plain read.
export const SESSION_STATUSES = [
  "extracting",
  "awaiting_classification",
  "deduping",
  "awaiting_dedup",
  "placing",
  "awaiting_orphans",
  "ingesting",
  "complete",
  "failed",
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/* * One object's lifecycle within a session (`bulk_objects.disposition`). */
export const OBJECT_DISPOSITIONS = [
  "pending",
  "accepted",
  "rejected",
  "orphaned",
  "ingested",
] as const;
export type ObjectDisposition = (typeof OBJECT_DISPOSITIONS)[number];

export const BULK_SOURCES = ["upload", "johndeere"] as const;
export type BulkSource = (typeof BULK_SOURCES)[number];

// Four user-facing steps, each with the status the server is in while working toward it and the status it parks
// in when it needs an answer. Deriving the stepper from this table (rather than a hand-kept index) means a new
// server status can only ever fall through to "unknown", never silently render the wrong screen.
export interface WizardStep {
  key: "classify" | "dedup" | "orphans" | "import";
  label: string;
  working: SessionStatus;
  waiting: SessionStatus | null;
}
export const WIZARD_STEPS: WizardStep[] = [
  {
    key: "classify",
    label: "Check what we found",
    working: "extracting",
    waiting: "awaiting_classification",
  },
  {
    key: "dedup",
    label: "Resolve overlaps",
    working: "deduping",
    waiting: "awaiting_dedup",
  },
  {
    key: "orphans",
    label: "Review leftovers",
    working: "placing",
    waiting: "awaiting_orphans",
  },
  { key: "import", label: "Import", working: "ingesting", waiting: null },
];

export interface SessionView {
  /* * index into WIZARD_STEPS, or WIZARD_STEPS.length once complete; -1 when the status is unrecognised */
  index: number;
  step: WizardStep | null;
  /* * true = parked on the user; the wizard shows the decision screen rather than a progress bar */
  waiting: boolean;
  done: boolean;
  failed: boolean;
}
export function sessionView(status: string | null | undefined): SessionView {
  const base = { step: null, waiting: false, done: false, failed: false };
  // Guard FIRST: the `import` step has `waiting: null`, so a missing status would otherwise match it via
  // `s.waiting === status` and render "Import" as in progress for a session we know nothing about.
  if (typeof status !== "string" || !status) return { ...base, index: -1 };
  if (status === "complete")
    return { ...base, index: WIZARD_STEPS.length, done: true };
  // A failure keeps NO position — the wizard shows the error, not a half-finished stepper claiming progress.
  if (status === "failed") return { ...base, index: -1, failed: true };
  const i = WIZARD_STEPS.findIndex(
    (s) => s.working === status || s.waiting === status,
  );
  if (i < 0) return { ...base, index: -1 };
  return {
    ...base,
    index: i,
    step: WIZARD_STEPS[i],
    waiting: WIZARD_STEPS[i].waiting === status,
  };
}

/*
 *
 * Poll cadence for a session, in ms — `false` means stop.
 *
 * The point is to stop polling the moment the server is NOT working: a session parked in an `awaiting_*`
 * status is waiting on the human in front of the screen and will not change on its own, so re-asking every
 * few seconds is pure noise. Same for the terminal states.
 */
export function sessionPollInterval(
  status: string | null | undefined,
): number | false {
  const v = sessionView(status);
  if (v.done || v.failed) return false;
  if (v.index < 0) return 5000; // unknown/not-started-yet: keep looking, the row may not exist yet
  if (v.waiting) return false; // parked on the user
  return v.step?.key === "import" ? 5000 : 3000;
}

// ---- Display helpers for the session screens -------------------------------
// `acres` and `point_count` are mutually exclusive by `is_areal` (BE-Light: polygons carry acres, point
// layers carry a count), so one function reads whichever applies rather than the caller branching.
export function objectSizeText(o: {
  is_areal?: boolean | null;
  acres?: number | null;
  point_count?: number | null;
}): string {
  if (typeof o.acres === "number" && Number.isFinite(o.acres)) {
    return `${o.acres >= 100 ? Math.round(o.acres) : Math.round(o.acres * 10) / 10} ac`;
  }
  if (typeof o.point_count === "number" && Number.isFinite(o.point_count)) {
    return `${o.point_count.toLocaleString()} point${o.point_count === 1 ? "" : "s"}`;
  }
  return "";
}

/*
 *
 * Where an object landed. An object inside an ACCEPTED in-batch candidate is placed against the CANDIDATE
 * (`placed_object_id`), not a field — the field doesn't exist until ingest. Without naming that case it
 * renders as a containerless row, which reads exactly like an orphan; compute and BE-Light both flagged it
 * and BE-Light added `placed_object_name` so we can say "inside North 40 (this drop)".
 */
export function containerText(o: {
  placed_field_name?: string | null;
  placed_object_name?: string | null;
}): string {
  if (o.placed_field_name) return `in ${o.placed_field_name}`;
  if (o.placed_object_name) return `in ${o.placed_object_name} (this drop)`;
  return "";
}

/*
 *
 *: an unextractable file must be flagged BY NAME.
 */
export function fileStatusText(f: {
  status?: string | null;
  error?: string | null;
  object_count?: number;
}): string {
  if (f.status === "failed") return f.error || "Couldn’t be read";
  if (f.status === "parsed") {
    const n = f.object_count ?? 0;
    return `${n} object${n === 1 ? "" : "s"}`;
  }
  return "Waiting…";
}

/*
 *
 * The row label for an extracted object: `feature_name ?? layer_name ?? original_filename`, with the class
 * carried alongside (we render it as a badge, so it isn't repeated in the text when a real name exists).
 *
 * History worth keeping, because it constrains what this may do: on the FIRST capture every object came back
 * unnamed while the names sat in `attributes` (`{"FieldName": …}`). Compute root-caused two defects — an
 * exact-key name lookup that never matched `FieldName`, and `layer_name` being structurally impossible for
 * GeoJSON — and fixed both. Named FEATURES now carry `feature_name`; **point layers legitimately do not**
 * (a 700-point EC scan is one object with no single feature name), so `null` there is correct and permanent
 * rather than a gap to paper over.
 *
 * This does NOT sniff `attributes` for a name, and must not start: naming is classification logic that
 * belongs to compute — BE-Light declined to derive it in their relay for the same reason. Compute also
 * deliberately declined to synthesise a display label like "Polygon 1 of 3", because composing one is
 * display logic and therefore ours.
 */
export function objectLabel(o: {
  feature_name?: string | null;
  layer_name?: string | null;
  effective_class?: string | null;
  original_filename?: string | null;
}): string {
  const named = o.feature_name || o.layer_name;
  if (named) return named;
  const cls = o.effective_class ? classLabel(o.effective_class) : "Object";
  return o.original_filename ? `${cls} from ${o.original_filename}` : cls;
}

/*
 *
 * ? observed_on`; compute reads it from attributes (`Sample_Date`, `Date_Sampled`, `Collected`, …)
 * and leaves it NULL rather than guessing. Undated is the normal case for zones and EC scans, so
 * absence is not a problem to flag here.
 */
export function observedOnText(o: {
  effective_observed_on?: string | null;
}): string {
  return o.effective_observed_on ? `sampled ${o.effective_observed_on}` : "";
}

/*
 *
 * " Fields build CONCURRENTLY (GEE-gated at 2 fleet-wide), so the cost is sub-linear — never phrase
 * this as per-field, which would promise 24 hours for 24 fields.
 */
export function buildWaitText(fieldCount?: number | null): string {
  if (
    typeof fieldCount !== "number" ||
    !Number.isFinite(fieldCount) ||
    fieldCount <= 0
  ) {
    return "about an hour for a single field, up to a day for dozens";
  }
  if (fieldCount === 1) return "about an hour";
  if (fieldCount < 12) return "a few hours";
  return "up to a day";
}
